import { forwardRef, useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import emailjs from "@emailjs/browser";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { CONTACT } from "@/data/contact";
import { PLANS, PLAN_BY_ID, type PlanId } from "@/data/pricing";

const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID as string;
const EMAILJS_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID as string;
const EMAILJS_PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY as string;

const FIELD_CLASS =
  "h-14 bg-white/10 border-white/20 text-white placeholder:text-white/50 text-base rounded-xl focus-visible:ring-accent focus-visible:ring-2";

interface PricingEnquiryFormProps {
  plan: PlanId;
  onPlanChange: (plan: PlanId) => void;
  message: string;
  onMessageChange: (message: string) => void;
}

/**
 * Enquiry form for the pricing page. The selected plan and the drafted
 * message are owned by the page, so choosing a plan above the form (or
 * here) re-drafts the message. Sends through the same EmailJS template as
 * the contact form.
 */
export const PricingEnquiryForm = forwardRef<HTMLInputElement, PricingEnquiryFormProps>(
  function PricingEnquiryForm({ plan, onPlanChange, message, onMessageChange }, nameRef) {
    const { toast } = useToast();
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!name || !phone || !message) return;

      setIsSubmitting(true);
      const planName = PLAN_BY_ID[plan].name;
      const fullMessage = `Plan: ${planName}\nName: ${name}\nPhone: ${phone}\nEmail: ${email || "Not provided"}\n\nMessage:\n${message}`;

      try {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            to_email: CONTACT.email,
            from_name: name,
            from_email: email || "Not provided",
            phone,
            message: fullMessage,
            subject: `Pricing Enquiry (${planName}) from ${name}`,
          },
          EMAILJS_PUBLIC_KEY
        );

        setSubmitted(true);
        toast({ title: "Enquiry Sent Successfully!", description: "We'll get back to you soon." });
        setName("");
        setPhone("");
        setEmail("");
        onMessageChange(PLAN_BY_ID[plan].draft);
        setTimeout(() => setSubmitted(false), 3000);
      } catch (error: unknown) {
        console.error("EmailJS Error:", error);
        const errMsg = error && typeof error === "object" && "text" in error
          ? String((error as { text: unknown }).text)
          : String(error);
        toast({
          title: "Failed to Send Enquiry",
          description: errMsg || "Please try again later or contact us directly.",
          variant: "destructive",
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 text-left">
        <fieldset>
          <legend className="text-sm font-semibold text-white/80 mb-2">Plan</legend>
          <div className="grid grid-cols-3 gap-2">
            {PLANS.map((p) => (
              <label
                key={p.id}
                className={cn(
                  "cursor-pointer rounded-xl border px-3 py-3 text-center text-sm font-semibold transition-colors focus-within:ring-2 focus-within:ring-accent",
                  plan === p.id
                    ? "bg-accent border-accent text-white"
                    : "bg-white/10 border-white/20 text-white/80 hover:bg-white/15"
                )}
              >
                <input
                  type="radio"
                  name="enquiry-plan"
                  value={p.id}
                  checked={plan === p.id}
                  onChange={() => onPlanChange(p.id)}
                  className="sr-only"
                />
                {p.name}
              </label>
            ))}
          </div>
        </fieldset>
        <Input
          ref={nameRef}
          type="text"
          placeholder="Your name"
          aria-label="Your name"
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={FIELD_CLASS}
          required
        />
        <Input
          type="tel"
          placeholder="Contact number"
          aria-label="Contact number"
          autoComplete="tel"
          inputMode="tel"
          // At least 10 digits, allowing a +91 prefix, spaces and dashes.
          pattern="[+]?[0-9][0-9\s\-]{8,}[0-9]"
          title="Please enter a valid phone number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className={FIELD_CLASS}
          required
        />
        <Input
          type="email"
          placeholder="Email address (optional)"
          aria-label="Email address (optional)"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={FIELD_CLASS}
        />
        <Textarea
          aria-label="Your message"
          value={message}
          onChange={(e) => onMessageChange(e.target.value)}
          rows={9}
          className="bg-white/10 border-white/20 text-white placeholder:text-white/50 text-base rounded-xl focus-visible:ring-accent focus-visible:ring-2 resize-y"
          required
        />
        <Button
          variant="ghost"
          type="submit"
          className="btn-fx h-14 w-full bg-brand hover:bg-brand text-white rounded-full font-semibold hover:-translate-y-0.5 hover:shadow-[0_14px_40px_-12px_rgba(91,124,250,.8)] transition-all text-base transition-all mt-1"
          disabled={submitted || isSubmitting}
        >
          {submitted ? (
            <>
              <CheckCircle2 className="w-5 h-5 mr-2" /> Sent!
            </>
          ) : isSubmitting ? (
            "Sending..."
          ) : (
            <>
              Send Enquiry <ArrowRight className="w-5 h-5 ml-2" />
            </>
          )}
        </Button>
      </form>
    );
  }
);
